import React from 'react';
import type { ScoreZone, SecurityLevel } from '@/types/auth';
import { getScoreZone, getZoneColor, formatScore, SECURITY_PRESETS } from '@/lib/score-utils';

interface ScoreMeterProps {
  /** Score value 0-100 */
  score: number;
  /** Security level for zone thresholds */
  securityLevel?: SecurityLevel;
  /** Show zone labels */
  showLabels?: boolean;
  /** Show threshold markers */
  showThresholds?: boolean;
  /** Height of the bar */
  height?: number;
  /** Animate the indicator */
  animate?: boolean;
}

/**
 * ScoreMeter — Horizontal 3-zone bar with circular indicator.
 * Zones: 0-50 danger (red), 50-70 warning (yellow), 70-100 success (green)
 */
export default function ScoreMeter({
  score,
  securityLevel = 'balanced',
  showLabels = true,
  showThresholds = true,
  height = 12,
  animate = true,
}: ScoreMeterProps) {
  const config = SECURITY_PRESETS[securityLevel];
  const zone = getScoreZone(score, securityLevel);
  const zoneColor = getZoneColor(zone);
  const clampedScore = Math.max(0, Math.min(100, score));
  const percentage = clampedScore;

  const rejectThresh = config.rejectThreshold;
  const acceptThresh = config.acceptThreshold;

  return (
    <div className="w-full">
      {/* Labels */}
      {showLabels && (
        <div className="flex items-center justify-between mb-2 text-xs font-sans">
          <span className="text-accent-danger">Rejeitado</span>
          <span className="text-accent-warning">Zona Cinza</span>
          <span className="text-accent-success">Aceito</span>
        </div>
      )}

      {/* Bar container */}
      <div className="relative" style={{ height: height + 16 }}>
        {/* Background bar with 3 zones */}
        <div
          className="w-full rounded-full overflow-hidden flex"
          style={{ height }}
        >
          {/* Reject zone */}
          <div
            className="bg-accent-danger/30"
            style={{ width: `${rejectThresh}%` }}
          />
          {/* Gray zone */}
          <div
            className="bg-accent-warning/30"
            style={{ width: `${acceptThresh - rejectThresh}%` }}
          />
          {/* Accept zone */}
          <div
            className="bg-accent-success/30"
            style={{ width: `${100 - acceptThresh}%` }}
          />
        </div>

        {/* Filled portion */}
        <div
          className="absolute top-0 left-0 rounded-full overflow-hidden"
          style={{
            height,
            width: `${percentage}%`,
            transition: animate ? 'width 0.8s ease-out' : 'none',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              background: `linear-gradient(90deg, #e74c3c 0%, #f5a623 ${rejectThresh}%, #f5a623 ${acceptThresh}%, #00d26a 100%)`,
            }}
          />
        </div>

        {/* Threshold markers */}
        {showThresholds && (
          <>
            <div
              className="absolute top-0 w-[2px] bg-txt-muted/50"
              style={{ height: height + 8, left: `${rejectThresh}%` }}
            />
            <div
              className="absolute top-0 w-[2px] bg-txt-muted/50"
              style={{ height: height + 8, left: `${acceptThresh}%` }}
            />
          </>
        )}

        {/* Circular indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2"
          style={{
            left: `${percentage}%`,
            transform: `translate(-50%, -50%)`,
            transition: animate ? 'left 0.8s ease-out' : 'none',
          }}
        >
          <div
            className="w-6 h-6 rounded-full border-3 border-bg-primary shadow-lg flex items-center justify-center"
            style={{
              backgroundColor: zoneColor,
              boxShadow: `0 0 12px ${zoneColor}60`,
            }}
          >
            <div className="w-2 h-2 rounded-full bg-white/80" />
          </div>
        </div>
      </div>

      {/* Score display */}
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-txt-muted font-sans">0</span>
        <span
          className="text-lg font-bold font-mono"
          style={{ color: zoneColor }}
        >
          {formatScore(clampedScore)}
        </span>
        <span className="text-xs text-txt-muted font-sans">100</span>
      </div>
    </div>
  );
}
